import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Play, Pause, Flag } from 'lucide-react';
import { useConfirm } from '@/shared/components/confirm';
import { Button } from '@/shared/ui/button';
import type { NpsSurvey, SurveyStatus } from '@/shared/types/nps';
import { updateSurveyStatus } from '../../api/use-nps';

interface Props {
  survey: NpsSurvey;
  onUpdated?: () => void;
}

export function SurveyStatusActions({ survey, onUpdated }: Props) {
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const changeStatus = async (status: SurveyStatus) => {
    try {
      await updateSurveyStatus(survey.id, status);
      toast.success('Status atualizado.');
      queryClient.invalidateQueries({ queryKey: ['nps', 'surveys'] });
      queryClient.invalidateQueries({ queryKey: ['nps', 'survey', survey.id] });
      onUpdated?.();
    } catch {
      toast.error('Falha ao alterar status.');
    }
  };

  const ask = async (title: string, description: string, status: SurveyStatus) => {
    const ok = await confirm({ title, description, confirmLabel: 'Confirmar' });
    if (ok) await changeStatus(status);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {survey.status === 'DRAFT' ? (
        <Button size="sm" onClick={() => ask('Publicar pesquisa?', 'A pesquisa será publicada. Admins ainda poderão editar textos e traduções.', 'ACTIVE')}>
          <Play className="size-4" /> Publicar
        </Button>
      ) : null}

      {survey.status === 'ACTIVE' || survey.status === 'PAUSED' ? (
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            ask(
              survey.status === 'ACTIVE' ? 'Pausar pesquisa?' : 'Retomar pesquisa?',
              survey.status === 'ACTIVE'
                ? 'A pesquisa ficará indisponível para novas respostas.'
                : 'A pesquisa voltará a aceitar respostas.',
              survey.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
            )
          }
        >
          {survey.status === 'ACTIVE' ? <Pause className="size-4" /> : <Play className="size-4" />}
          {survey.status === 'ACTIVE' ? 'Pausar' : 'Retomar'}
        </Button>
      ) : null}

      {survey.status === 'ACTIVE' ? (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => ask('Finalizar pesquisa?', 'A pesquisa será encerrada definitivamente.', 'FINISHED')}
        >
          <Flag className="size-4" /> Finalizar
        </Button>
      ) : null}
    </div>
  );
}
